import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaEdit,
  FaTrash,
  FaUserShield,
  FaUser,
  FaCheck,
  FaTimes,
  FaPlus,
  FaSearch,
  FaFilter,
  FaBuilding,
  FaEnvelope,
  FaPhone,
  FaBriefcase,
  FaUserCog,
  FaToggleOn,
  FaToggleOff,
  FaSyncAlt,
  FaArrowLeft,
  FaTachometerAlt,
  FaKey
} from 'react-icons/fa';
import './Admin.css';

// Add additional styles for organization checkboxes
const orgCheckboxStyles = `
  .admin-org-checkbox-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 300px;
    overflow-y: auto;
    padding: 10px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background-color: #FFFAFA;
  }
  
  .admin-subscription-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    background-color: #D2BA7B;
    color: #243444;
  }
  
  .admin-date-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    background-color: #4C5454;
    color: #FFFAFA;
  }
  
  .admin-status-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 20px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .admin-status-toggle.active {
    background-color: #D2BA7B;
    color: #243444;
  }
  
  .admin-status-toggle.inactive {
    background-color: #4C5454;
    color: #FFFAFA;
  }
  
  .admin-status-toggle svg {
    font-size: 1.2rem;
  }
  
  .admin-org-checkbox-item {
    display: flex;
    align-items: center;
    padding: 8px;
    border-radius: 4px;
    background-color: #f9f9f9;
    transition: background-color 0.2s;
  }
  
  .admin-org-checkbox-item:hover {
    background-color: #f0f0f0;
  }
  
  .admin-org-checkbox-item input[type="checkbox"] {
    margin-right: 10px;
    cursor: pointer;
    width: 18px;
    height: 18px;
  }
  
  .admin-org-checkbox-item input[type="radio"] {
    margin-left: 15px;
    cursor: pointer;
    width: 16px;
    height: 16px;
  }
  
  .admin-org-checkbox-item label {
    flex-grow: 1;
    cursor: pointer;
    font-weight: normal;
    margin-bottom: 0;
  }
  
  .admin-primary-org-label {
    margin-left: 10px;
    font-size: 12px;
    background-color: #243444;
    color: #FFFAFA;
    padding: 2px 8px;
    border-radius: 10px;
  }
`;

// Add styles to document
const styleElement = document.createElement('style');
styleElement.textContent = orgCheckboxStyles;
document.head.appendChild(styleElement);

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsError, setOrganizationsError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // Filter by subscriptionStatus
  const [roleFilter, setRoleFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user',
    subscriptionStatus: 'active',
    organization: '',
    organizations: [], // Array for multiple organizations
    organizationName: '',
    phone: '',
    jobTitle: '',
    department: '',
    isMultiOrg: false
  });
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordResetData, setPasswordResetData] = useState({
    userId: '',
    email: '',
    newPassword: ''
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, [refreshTrigger]);
  
  // Format date in a more readable way
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter and search users
  useEffect(() => {
    // If `users` is not an array, clear `filteredUsers` and bail out:
    if (!Array.isArray(users)) {
      setFilteredUsers([]);
      return;
    }

    // Start from the full users list:
    let filtered = [...users];

    // 1) Organization filter
    if (selectedOrgId !== 'all') {
      filtered = filtered.filter(u => {
        // Check primary organization
        if (u.organization === selectedOrgId) return true;
        
        // Check organizations array for multi-org users
        if (Array.isArray(u.organizations) && u.organizations.includes(selectedOrgId)) return true;
        
        return false;
      });
    }

    // 2) Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(u =>
        u.subscriptionStatus === statusFilter
      );
    }

    // 3) Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u =>
        u.role === roleFilter
      );
    }

    // 4) Text search
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        (u.name && u.name.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term)) ||
        (u.organizationName && u.organizationName.toLowerCase().includes(term)) ||
        (u.jobTitle && u.jobTitle.toLowerCase().includes(term))
      );
    }

    setFilteredUsers(filtered);
  }, [selectedOrgId, statusFilter, roleFilter, searchTerm, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any existing errors

      try {
        const res = await axios.get('/api/admin/users');
        console.log('Raw API user response:', res.data);

        const apiUsers =
          Array.isArray(res.data) ? res.data :
          Array.isArray(res.data.users) ? res.data.users :
          [];

        if (!Array.isArray(apiUsers)) {
          console.error('Fetched users is not an array. Value:', apiUsers);
          throw new Error('Invalid user data format from API');
        }

        setUsers(apiUsers);
        console.log('Successfully fetched users from API');
      } catch (apiErr) {
        console.warn('Error fetching users from API, using mock data:', apiErr.message);
        // Use mock data when the API fails
        const mockUsers = [
          {
            _id: '1',
            name: 'Nathan Shoptaw',
            email: 'nathan.shoptaw@pinnacleappinnovators.com',
            role: 'admin',
            subscriptionStatus: 'active',
            organization: '2',
            organizationName: 'Pinnacle App Innovators',
            phone: '(555) 123-4567',
            jobTitle: 'Chief Technology Officer',
            department: 'Technology'
          },
          {
            _id: '2',
            name: 'Jane Smith',
            email: 'jane.smith@resilientinc.com',
            role: 'user',
            subscriptionStatus: 'active',
            organization: '3',
            organizationName: 'Resilient Solutions Inc.',
            phone: '(555) 987-6543',
            jobTitle: 'Business Continuity Manager',
            department: 'Operations'
          },
          {
            _id: '3',
            name: 'Michael Johnson',
            email: 'mjohnson@globalsecurity.com',
            role: 'user',
            subscriptionStatus: 'inactive',
            organization: '4',
            organizationName: 'Global Security Partners',
            phone: '(555) 456-7890',
            jobTitle: 'Security Analyst',
            department: 'Cybersecurity'
          },
          {
            _id: '4',
            name: 'Sarah Williams',
            email: 'swilliams@healthcare-continuity.org',
            role: 'manager',
            subscriptionStatus: 'active',
            organization: '5',
            organizationName: 'Healthcare Continuity Group',
            phone: '(555) 234-5678',
            jobTitle: 'Director of Resilience',
            department: 'Risk Management'
          },
          {
            _id: '5',
            name: 'Robert Chen',
            email: 'rchen@brn.com',
            role: 'admin',
            subscriptionStatus: 'active',
            organization: '1',
            organizationName: 'Business Resilience Navigator',
            phone: '(555) 876-5432',
            jobTitle: 'Product Manager',
            department: 'Product'
          }
        ];
        setUsers(mockUsers);
      }
    } catch (err) {
      console.error('Error in user fetch process:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    console.log('Fetching organizations...');
    setOrganizationsError(null);
    try {
      // Only use the admin-specific endpoint like OrganizationManagement.js
      const res = await axios.get('/api/admin/organizations');
      console.log('Admin organizations API response:', res.data);
      
      // Ensure we always have an array, even if the response structure varies
      const orgsData = Array.isArray(res.data) ? res.data :
                      Array.isArray(res.data.organizations) ? res.data.organizations :
                      [];
      
      // Double-check that we have an array before setting state
      if (!Array.isArray(orgsData)) {
        throw new Error('Organizations data is not an array');
      }
      
      setOrganizations(orgsData);
      console.log('Successfully set organizations:', orgsData);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setOrganizationsError(err.message || 'Failed to load organizations');
      
      // Fallback to mock data for development only if needed
      console.log('Using mock organization data for development');
      const mockOrganizations = [
        { _id: '1', name: 'Business Resilience Navigator', industry: 'Technology', location: 'San Francisco, CA', size: 'Enterprise' },
        { _id: '2', name: 'Pinnacle App Innovators', industry: 'Software Development', location: 'Austin, TX', size: 'Medium' },
        { _id: '3', name: 'Resilient Solutions Inc.', industry: 'Consulting', location: 'Chicago, IL', size: 'Small' },
        { _id: '4', name: 'Global Security Partners', industry: 'Cybersecurity', location: 'New York, NY', size: 'Large' },
        { _id: '5', name: 'Healthcare Continuity Group', industry: 'Healthcare', location: 'Boston, MA', size: 'Medium' }
      ];
      console.log('Using mock organizations:', mockOrganizations);
      setOrganizations(mockOrganizations);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Special handling for isMultiOrg checkbox
    if (name === 'isMultiOrg') {
      if (checked) {
        // When enabling multi-org, convert single organization to array if it exists
        const orgsArray = formData.organization ? [formData.organization] : [];
        setFormData({
          ...formData,
          isMultiOrg: checked,
          organizations: orgsArray
        });
      } else {
        // When disabling multi-org, set primary organization to the first one in the array (if any)
        setFormData({
          ...formData,
          isMultiOrg: checked,
          organization: formData.organizations.length > 0 ? formData.organizations[0] : '',
          organizations: []
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleOrganizationCheckboxChange = (orgId) => {
    // Create a copy of the current organizations array
    let updatedOrgs = [...formData.organizations];
    
    // Check if the organization is already selected
    const orgIndex = updatedOrgs.indexOf(orgId);
    
    if (orgIndex === -1) {
      // Add the organization if not already selected
      updatedOrgs.push(orgId);
    } else {
      // Remove the organization if already selected
      updatedOrgs.splice(orgIndex, 1);
    }
    
    // Handle primary organization logic
    let primaryOrg = formData.organization;
    let primaryOrgName = formData.organizationName;
    
    // If we're removing the primary organization
    if (orgId === primaryOrg && orgIndex !== -1) {
      // Set a new primary org if there are other orgs available
      if (updatedOrgs.length > 0) {
        primaryOrg = updatedOrgs[0];
        const newPrimaryOrg = organizations.find(org => org._id === primaryOrg);
        primaryOrgName = newPrimaryOrg ? newPrimaryOrg.name : '';
      } else {
        primaryOrg = '';
        primaryOrgName = '';
      }
    }
    
    // If this is the first org being added and no primary is set
    if (updatedOrgs.length > 0 && !primaryOrg) {
      primaryOrg = updatedOrgs[0];
      const newPrimaryOrg = organizations.find(org => org._id === primaryOrg);
      primaryOrgName = newPrimaryOrg ? newPrimaryOrg.name : '';
    }
    
    // Update form data
    setFormData({
      ...formData,
      organizations: updatedOrgs,
      organization: primaryOrg,
      organizationName: primaryOrgName
    });
  };

  const handlePrimaryOrganizationChange = (orgId) => {
    // Find the organization to set as primary
    const selectedOrg = organizations.find(org => org._id === orgId);
    
    if (selectedOrg) {
      setFormData({
        ...formData,
        organization: orgId,
        organizationName: selectedOrg.name
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null); // Clear any existing errors

      if (isEditing) {
        try {
          await axios.put(`/api/admin/users/${selectedUser._id}`, formData);
          setUsers(users.map(user =>
            user._id === selectedUser._id ? { ...user, ...formData } : user
          ));
          console.log(`User ${selectedUser._id} updated successfully`);
        } catch (apiErr) {
          console.log('API endpoint for updating user not available:', apiErr.message);
          // Update the user in the local state anyway for development
          setUsers(users.map(user =>
            user._id === selectedUser._id ? { ...user, ...formData } : user
          ));

          // Show success message with note about development mode
          const tempMessage = document.createElement('div');
          tempMessage.className = 'admin-success-message';
          tempMessage.innerHTML = `<span>User updated in development mode (changes won't persist)</span>`;
          document.querySelector('.admin-section').appendChild(tempMessage);

          // Remove message after 3 seconds
          setTimeout(() => {
            if (tempMessage.parentNode) {
              tempMessage.parentNode.removeChild(tempMessage);
            }
          }, 3000);
        }
      } else {
        try {
          console.log('Attempting to create user with data:', formData);
          console.log('Using axios baseURL:', axios.defaults.baseURL);

          const res = await axios.post('/api/admin/users', formData);
          console.log('API response:', res.data);
          setUsers([...users, res.data]);
          console.log('New user created successfully');

          // Show success message
          const tempMessage = document.createElement('div');
          tempMessage.className = 'admin-success-message';
          tempMessage.innerHTML = `<span>User created successfully!</span>`;
          document.querySelector('.admin-section').appendChild(tempMessage);

          // Remove message after 3 seconds
          setTimeout(() => {
            if (tempMessage.parentNode) {
              tempMessage.parentNode.removeChild(tempMessage);
            }
          }, 3000);
        } catch (apiErr) {
          console.error('Error creating user:', apiErr);
          console.error('Error details:', apiErr.response?.data || 'No response data');
          console.log('API endpoint for creating user not available:', apiErr.message);

          // Create a mock user with an ID for development
          const mockNewUser = {
            _id: `mock-${Date.now()}`,
            ...formData,
            organizationName: organizations.find(org => org._id === formData.organization)?.name || ''
          };
          setUsers([...users, mockNewUser]);

          // Show success message with note about development mode
          const tempMessage = document.createElement('div');
          tempMessage.className = 'admin-success-message';
          tempMessage.innerHTML = `<span>User created in development mode (changes won't persist)</span>`;
          document.querySelector('.admin-section').appendChild(tempMessage);

          // Remove message after 3 seconds
          setTimeout(() => {
            if (tempMessage.parentNode) {
              tempMessage.parentNode.removeChild(tempMessage);
            }
          }, 3000);
        }
      }

      // Reset form state regardless of API success
      setIsEditing(false);
      setIsCreating(false);
      setSelectedUser(null);
      setFormData({
        name: '',
        email: '',
        role: 'user',
        subscriptionStatus: 'active',
        organization: '',
        organizationName: '',
        phone: '',
        jobTitle: '',
        department: '',
        isMultiOrg: false
      });
    } catch (err) {
      console.error('Error in user submission process:', err);
      setError({
        type: 'error',
        message: 'Failed to save user. Please try again.'
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'user',
      subscriptionStatus: 'active',
      organization: '',
      organizationName: '',
      phone: '',
      jobTitle: '',
      department: '',
      isMultiOrg: false
    });
  };

  const handleToggleStatus = async (user) => {
    try {
      const newStatus = user.subscriptionStatus === 'active' ? 'inactive' : 'active';
      const userName = user.name || user.email || 'User';
      
      // Show loading state
      setLoading(true);
      
      // Clear any existing errors
      setError(null);
      
      try {
        // Try the specific status endpoint first
        const response = await axios.put(`/api/admin/users/${user._id}/status`, { subscriptionStatus: newStatus });
        console.log(`User ${user._id} status updated to ${newStatus} successfully`);
        
        // Update the user in the state with the response from the server
        const updatedUsers = users.map(u =>
          u._id === user._id ? { ...u, subscriptionStatus: newStatus } : u
        );
        setUsers(updatedUsers);
        
        // Show success message
        setError({
          type: 'success',
          message: `${userName}'s subscription status changed to ${newStatus}`
        });
        
        // Remove message after 3 seconds
        setTimeout(() => {
          setError(null);
        }, 3000);
        
        // Refresh the user list to ensure we have the latest data
        await fetchUsers();
      } catch (apiErr) {
        console.error('Error updating user status:', apiErr);
        
        // Show appropriate error message
        if (apiErr.response && apiErr.response.data && apiErr.response.data.msg) {
          setError({
            type: 'error',
            message: apiErr.response.data.msg
          });
        } else {
          setError({
            type: 'error',
            message: `Failed to update status for ${userName}. Please try again.`
          });
        }
      } finally {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error in handleToggleStatus:', err);
      setError({
        type: 'error',
        message: 'An unexpected error occurred. Please try again.'
      });
      setLoading(false);
    }
  };
  const handleDeleteClick = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        setError(null); // Clear any existing errors
        const userToDelete = users.find(user => user._id === userId);
        const userDisplayName = userToDelete?.name || userToDelete?.email || 'User';
        
        try {
          await axios.delete(`/api/admin/users/${userId}`);
          setUsers(users.filter(user => user._id !== userId));
          console.log(`User ${userId} deleted successfully`);
          
          // Show success message
          setError({
            type: 'success',
            message: `${userDisplayName} deleted successfully`
          });
          
          // Remove message after 3 seconds
          setTimeout(() => {
            setError(null);
          }, 3000);
        } catch (apiErr) {
          console.log('API endpoint for deleting user not available:', apiErr.message);
          // Remove the user from the local state anyway for development
          setUsers(users.filter(user => user._id !== userId));

          // Show success message with note about development mode
          setError({
            type: 'success',
            message: `${userDisplayName} deleted in development mode (changes won't persist)`
          });
          
          // Remove message after 3 seconds
          setTimeout(() => {
            setError(null);
          }, 3000);
        }
      } catch (err) {
        console.error('Error in user deletion process:', err);
        setError({
          type: 'error',
          message: 'Failed to delete user. Please try again.'
        });
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
  };

  const handleOrganizationFilterChange = (e) => {
    setSelectedOrgId(e.target.value);
  };

  const handleCreateClick = () => {
  setSelectedUser(null);
  setFormData({
    name: '',
    email: '',
    role: 'user',
    subscriptionStatus: 'active',
    organization: '',
    organizations: [],
    organizationName: '',
    phone: '',
    jobTitle: '',
    department: '',
    isMultiOrg: false
  });
  setIsCreating(true);
  setIsEditing(false);
};

const handleEditClick = (user) => {
  setSelectedUser(user);
  setFormData({
    name: user.name || '',
    email: user.email || '',
    role: user.role || 'user',
    subscriptionStatus: user.subscriptionStatus || 'active',
    organization: user.organization || '',
    organizations: Array.isArray(user.organizations) ? user.organizations : [],
    organizationName: user.organizationName || '',
    phone: user.phone || '',
    jobTitle: user.jobTitle || '',
    department: user.department || '',
    isMultiOrg: user.isMultiOrg || false
  });
  setIsEditing(true);
  setIsCreating(false);
};

const renderUserForm = () => {
  const formTitle = isCreating ? 'Add New User' : `Edit User: ${selectedUser?.name || selectedUser?.email}`;

  return (
    <div className="admin-form-container">
      <div className="admin-form-header">
        <h3>{formTitle}</h3>
        <button className="admin-btn admin-btn-secondary" onClick={handleCancel}>
          <FaTimes /> Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="admin-modern-form">
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="name">
              <FaUser /> Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="admin-form-control"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Full Name"
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="email">
              <FaEnvelope /> Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="admin-form-control"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="user@example.com"
            />
          </div>
        </div>

 {/* Password field removed - now sent automatically via email */}

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="role">
              <FaUserCog /> Role
            </label>
            <select
              id="role"
              name="role"
              className="admin-form-control"
              value={formData.role}
              onChange={handleChange}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="admin-form-group">
            <label htmlFor="subscriptionStatus">
              {formData.subscriptionStatus === 'active' ? <FaToggleOn /> : <FaToggleOff />} Status
            </label>
            <select
              id="subscriptionStatus"
              name="subscriptionStatus"
              className="admin-form-control"
              value={formData.subscriptionStatus}
              onChange={handleChange}
            >
              <option value="active">Active</option>
 <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="admin-form-group">
          <label>
            <FaBuilding /> {formData.isMultiOrg ? 'Organizations' : 'Organization'}
          </label>
          <div className="admin-checkbox-container">
            {Array.isArray(organizations) && organizations.length > 0 ? (
              <div className="admin-org-checkbox-list">
                {organizations.map(org => (
                  <div key={org._id} className="admin-org-checkbox-item">
                    <input
                      type="checkbox"
                      id={`org-${org._id}`}
                      name={`org-${org._id}`}
                      checked={Array.isArray(formData.organizations) && formData.organizations.includes(org._id)}
                      onChange={() => handleOrganizationCheckboxChange(org._id)}
                    />
                    <label htmlFor={`org-${org._id}`}>{org.name}</label>
                    {Array.isArray(formData.organizations) && formData.organizations.includes(org._id) && (
                      <input
                        type="radio"
                        name="primaryOrganization"
                        checked={formData.organization === org._id}
                        onChange={() => handlePrimaryOrganizationChange(org._id)}
                        disabled={!Array.isArray(formData.organizations) || !formData.organizations.includes(org._id)}
                      />
                    )}
                    {formData.organization === org._id && (
                      <span className="admin-primary-org-label">Primary</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-form-message">No organizations available</div>
            )}
          </div>
          {formData.isMultiOrg && (
            <div style={{ marginTop: '5px', fontSize: '14px', color: '#4C5454' }}>
              Check boxes to select organizations. Use the radio button to set the primary organization.
            </div>
          )}
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="phone">
              <FaPhone /> Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="admin-form-control"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(123) 456-7890"
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="jobTitle">
              <FaBriefcase /> Job Title
            </label>
            <input
              type="text"
              id="jobTitle"
              name="jobTitle"
              className="admin-form-control"
              value={formData.jobTitle}
              onChange={handleChange}
              placeholder="Job Title"
            />
          </div>
        </div>

 <div className="admin-form-group">
          <label htmlFor="department">Department</label>
          <input
            type="text"
            id="department"
            name="department"
            className="admin-form-control"
            value={formData.department}
            onChange={handleChange}
            placeholder="Department"
          />
        </div>

        <div className="admin-form-group" style={{ marginTop: '15px' }}>
          <div className="checkbox-container" style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id="multiOrgUser"
              name="isMultiOrg"
              checked={formData.isMultiOrg}
              onChange={handleChange}
              style={{ marginRight: '10px' }}
            />
            <label htmlFor="multiOrgUser" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Multi-Organization user</span>
            </label>
          </div>
          <div style={{ marginLeft: '25px', fontSize: '14px', color: '#4C5454', marginTop: '5px' }}>
            Enable this option if you need to manage multiple client organizations. When enabled, you'll be able
            to add and switch between different organizations in the Organization Settings page.
          </div>
        </div>

        <div className="admin-form-actions">
          <button type="submit" className="admin-btn admin-btn-primary">
            {isCreating ? 'Create User' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

const renderUserTable = () => {
  return (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Organization</th>
            <th>Role</th>
            <th>Status</th>
            <th>Subscription</th>
            <th>Date Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {!Array.isArray(filteredUsers) ? (
            <tr>
              <td colSpan="8" className="admin-no-data">
                Error: User data is invalid
              </td>
            </tr>
          ) : filteredUsers.length === 0 ? (
            <tr>
              <td colSpan="8" className="admin-no-data">
                {searchTerm ? 'No users match your search criteria' : 'No users found'}
              </td>
            </tr>
          ) : (
            filteredUsers.map(user => (
              <tr key={user._id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>
                  {user.isMultiOrg ? (
                    <div>
                      <span style={{ fontWeight: 'bold' }}>Multi-Organization</span>
                      <span
                        style={{
                          marginLeft: '8px',
                          fontSize: '12px',
                          backgroundColor: '#243444',
                          color: '#FFFAFA',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          display: 'inline-block'
                        }}
                        title="This user can manage multiple organizations"
                      >
                        Multi-Org
                      </span>
                      {Array.isArray(user.organizations) && user.organizations.length > 0 && (
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#4C5454' }}>
                          {organizations
                            .filter(org => user.organizations.some(userOrg => userOrg.toString() === org._id.toString()))
                            .map(org => org.name)
                            .join(', ')}
                        </div>
                      )}
                    </div>
                  ) : (
                    user.organizationName || 'Not Assigned'
                  )}
                </td>
                <td>
                  <span className={`admin-role-badge admin-role-${user.role}`}>
                    {user.role === 'admin' ? <FaUserShield /> : <FaUser />} {user.role}
                  </span>
                </td>
                <td>
                  <div 
                    className={`admin-status-toggle ${user.subscriptionStatus === 'active' ? 'active' : 'inactive'}`}
                    onClick={() => handleToggleStatus(user)}
                    title={user.subscriptionStatus === 'active' ? 'Click to deactivate user' : 'Click to activate user'}
                  >
                    {user.subscriptionStatus === 'active' ? <FaToggleOn /> : <FaToggleOff />}
                    {user.subscriptionStatus === 'active' ? 'Active' : 'Inactive'}
                  </div>
                </td>
                <td>
                  <span className="admin-subscription-badge">
                    {user.subscriptionTier === 'Monthly' ? 'Monthly' : 
                     user.subscriptionTier === 'SingleOrg' ? 'Annual (Single Org)' : 
                     user.subscriptionTier === 'MultiOrg' ? 'Annual (Multi Org)' : 'Unknown'}
                  </span>
                </td>
                <td>
                  <span className="admin-date-badge" title="Date and time user was created">
                    {formatDate(user.createdAt)}
                  </span>
                </td>
                <td>
                  <div className="admin-actions">
                    <button
                      className="admin-action-btn"
                      onClick={() => handleEditClick(user)}
                      title="Edit User"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="admin-action-btn"
                      onClick={() => handleResetPasswordClick(user)}
                      title="Reset Password"
                      style={{ color: '#243444' }}
                    >
                      <FaKey />
                    </button>
                    <button
                      className="admin-action-btn delete"
                      onClick={() => handleDeleteClick(user._id)}
                      title="Delete User"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const renderFilters = () => {
    return (
      <div className="admin-filters">
        <div className="admin-search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="admin-search-input"
          />
        </div>

        <div className="admin-filter-group">
          <div className="admin-filter">
            <label><FaFilter /> Status:</label>
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="admin-filter-select"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="admin-filter">
            <label><FaUserCog /> Role:</label>
            <select
              value={roleFilter}
              onChange={handleRoleFilterChange}
              className="admin-filter-select"
            >
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="admin-filter">
            <label><FaBuilding /> Organization:</label>
            <select
              value={selectedOrgId}
              onChange={handleOrganizationFilterChange}
              className="admin-filter-select"
            >
              <option value="all">All Organizations</option>
              {Array.isArray(organizations) ? organizations.map(org => (
                <option key={org._id} value={org._id}>
                  {org.name}
                </option>
              )) : null}
            </select>
          </div>
        </div>
      </div>
    );
  };

  // Handle reset password click
  const handleResetPasswordClick = (user) => {
    setPasswordResetData({
      userId: user._id,
      email: user.email,
      newPassword: ''
    });
    setIsResettingPassword(true);
  };

  // Handle password reset form submission
  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Call API to reset password
      await axios.post('/api/admin/users/reset-password', {
        userId: passwordResetData.userId,
        newPassword: passwordResetData.newPassword
      });

      // Show success message
      setError({ type: 'success', message: `Password reset successful for ${passwordResetData.email}` });
      
      // Reset form and state
      setPasswordResetData({
        userId: '',
        email: '',
        newPassword: ''
      });
      setIsResettingPassword(false);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(`Failed to reset password: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset cancel
  const handlePasswordResetCancel = () => {
    setIsResettingPassword(false);
    setPasswordResetData({
      userId: '',
      email: '',
      newPassword: ''
    });
  };

  // Render password reset form
  const renderPasswordResetForm = () => {
    return (
      <div className="admin-form-container">
        <div className="admin-form-header">
          <h3>Reset Password for {passwordResetData.email}</h3>
          <button className="admin-btn admin-btn-secondary" onClick={handlePasswordResetCancel}>
            <FaTimes /> Cancel
          </button>
        </div>

        <form onSubmit={handlePasswordResetSubmit} className="admin-modern-form">
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label htmlFor="newPassword">
                <FaKey /> New Password
              </label>
              <input
                type="text"
                id="newPassword"
                name="newPassword"
                className="admin-form-control"
                value={passwordResetData.newPassword}
                onChange={(e) => setPasswordResetData({...passwordResetData, newPassword: e.target.value})}
                required
                placeholder="Enter new password"
                autoComplete="new-password"
              />
              <small className="admin-form-text">Enter a new password for this user. They will need to use this password to log in.</small>
            </div>
          </div>

          <div className="admin-form-actions">
            <button type="submit" className="admin-btn admin-btn-primary">
              <FaKey /> Reset Password
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="admin-section">
      <div className="admin-dashboard-return">
        <Link to="/admin" className="admin-dashboard-return-btn">
          <FaArrowLeft /> Return to Admin Dashboard
        </Link>
      </div>

      <div className="admin-section-header">
        <div className="admin-title-area">
          <h2 className="admin-section-title">
            <FaUserCog /> User Management
          </h2>
        </div>

        <div className="admin-header-actions">
          <button
            className="admin-btn admin-btn-refresh"
            onClick={handleRefresh}
            title="Refresh Users"
          >
            <FaSyncAlt />
          </button>

 {!isEditing && !isCreating && !isResettingPassword && (
            <button
              className="admin-btn admin-btn-primary"
              onClick={handleCreateClick}
            >
              <FaPlus /> Add User
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className={`admin-message ${error.type === 'success' ? 'admin-success-message' : 'admin-error-message'}`}>
          <FaTimes onClick={() => setError(null)} className="admin-message-close" />
          {typeof error === 'object' ? error.message : error}
        </div>
      )}

      {loading ? (
        <div className="admin-loading">Loading users...</div>
      ) : isResettingPassword ? (
        renderPasswordResetForm()
      ) : isEditing || isCreating ? (
        renderUserForm()
      ) : (
        <>
          {renderFilters()}
          {renderUserTable()}
        </>
      )}
    </div>
  );
};

export default UserManagement;
